import { 
  VStack, 
  HStack, 
  Button, 
  Text, 
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Icon,
  Center,
  Switch,
  FormControl,
  FormLabel,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useAudioContext } from '../context/AudioContext';
import { useDisplayNames } from '../context/DisplayNamesContext';
import { FaSmile, FaMeh, FaFrown, FaCopy } from 'react-icons/fa';
import TooltipKeyBox from './TooltipKeyBox';

interface ComparisonResult {
  trackA: string;
  trackB: string;
  preference: 'better' | 'same' | 'worse';
}

interface TrackStats {
  better: number;
  worse: number;
  total: number;
}

interface EliminationRound {
  winner: string;
  loser: string;
  margin: 'clearly' | 'slightly';
  winnerPlayer: number;
  loserPlayer: number;
}

type CompareMethod = 'free' | 'narrow-down';

interface PickYourFaveViewProps {
  playerNumberToFileId?: Map<number, string>;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const assignLabels = (player1: number, player2: number): { a: number; b: number } => {
  if (Math.random() < 0.5) {
    return { a: player1, b: player2 };
  }
  return { a: player2, b: player1 };
};

const PickYourFaveView = ({ playerNumberToFileId }: PickYourFaveViewProps) => {
  const { audioElements, selectedPlayer, selectPlayer, playAll, pauseAll, blindTestMode, blindTestIncludedPlayers, isLooping } = useAudioContext();
  const { getCustomDisplayName } = useDisplayNames();
  const [compareMethod, setCompareMethod] = useState<CompareMethod>('free');
  const [comparisons, setComparisons] = useState<ComparisonResult[]>([]);
  const [previousTrack, setPreviousTrack] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [allowRepeats, setAllowRepeats] = useState(false);

  // Narrow Down state
  const [queue, setQueue] = useState<number[]>([]);
  const [labelA, setLabelA] = useState<number | null>(null);
  const [labelB, setLabelB] = useState<number | null>(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [eliminationPath, setEliminationPath] = useState<EliminationRound[]>([]);
  const [narrowDownWinner, setNarrowDownWinner] = useState<string | null>(null);
  const [showNarrowDownResults, setShowNarrowDownResults] = useState(false);

  const toast = useToast();

  const getIncludedPlayers = () =>
    Array.from({ length: audioElements.size }, (_, i) => i + 1)
      .filter(playerNum => blindTestIncludedPlayers.get(playerNum) ?? true);

  const getTrackName = (playerNum: number) => {
    const audio = audioElements.get(playerNum);
    if (!audio) return `Track ${playerNum}`;
    const fileId = playerNumberToFileId?.get(playerNum);
    const customName = fileId ? getCustomDisplayName(fileId) : '';
    return customName || audio.title;
  };

  const resetNarrowDown = () => {
    setQueue([]);
    setLabelA(null);
    setLabelB(null);
    setCurrentRound(0);
    setTotalRounds(0);
    setEliminationPath([]);
    setNarrowDownWinner(null);
    setShowNarrowDownResults(false);
  };

  // Reset component when tab is selected
  useEffect(() => {
    setComparisons([]);
    setPreviousTrack(null);
    setShowResults(false);
    setHasStarted(false);
    setIsComparing(false);
    setAllowRepeats(false);
    setCompareMethod('free');
    resetNarrowDown();
  }, [blindTestMode]);

  // Listen for random switcher button press (Free compare only)
  useEffect(() => {
    const handleRandomSwitch = () => {
      if (compareMethod === 'free' && hasStarted) {
        setIsComparing(true);
      }
    };

    window.addEventListener('randomSwitch', handleRandomSwitch);
    return () => window.removeEventListener('randomSwitch', handleRandomSwitch);
  }, [hasStarted, compareMethod]);

  // Keep tracks looping for the whole Narrow Down session
  useEffect(() => {
    const shouldLoop =
      hasStarted &&
      compareMethod === 'narrow-down' &&
      !showNarrowDownResults;

    audioElements.forEach(audio => {
      audio.loop = shouldLoop ? true : isLooping;
    });

    return () => {
      audioElements.forEach(audio => {
        audio.loop = isLooping;
      });
    };
  }, [hasStarted, compareMethod, showNarrowDownResults, audioElements, isLooping]);

  // Narrow Down hotkeys: / toggles A/B; Enter = Clearly; Option+Enter = Slightly
  useEffect(() => {
    if (!hasStarted || compareMethod !== 'narrow-down' || showNarrowDownResults) return;
    if (labelA == null || labelB == null) return;

    const toggleAB = () => {
      if (selectedPlayer === labelA) {
        selectPlayer(labelB);
      } else if (selectedPlayer === labelB) {
        selectPlayer(labelA);
      } else {
        selectPlayer(labelA);
      }
    };

    const preferCurrent = (margin: 'clearly' | 'slightly') => {
      if (selectedPlayer === labelA) {
        handlePrefer('A', margin);
      } else if (selectedPlayer === labelB) {
        handlePrefer('B', margin);
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement ||
          (event.target instanceof HTMLElement && event.target.contentEditable === 'true')) {
        return;
      }

      const key = event.key;

      // Toggle between the current A/B pair (intercept before global player cycling)
      if (
        !event.metaKey && !event.ctrlKey && !event.altKey &&
        (key === '/' || key === '?' || key === 'ArrowUp' || key === 'ArrowDown')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        toggleAB();
        return;
      }

      // Direct listen shortcuts
      if (!event.metaKey && !event.ctrlKey && !event.altKey && key === '1') {
        event.preventDefault();
        event.stopImmediatePropagation();
        selectPlayer(labelA);
        return;
      }
      if (!event.metaKey && !event.ctrlKey && !event.altKey && key === '2') {
        event.preventDefault();
        event.stopImmediatePropagation();
        selectPlayer(labelB);
        return;
      }

      // Prefer currently playing: Enter = Clearly, Option+Enter = Slightly
      if (!event.metaKey && !event.ctrlKey && key === 'Enter') {
        event.preventDefault();
        event.stopImmediatePropagation();
        preferCurrent(event.altKey ? 'slightly' : 'clearly');
      }
    };

    // Capture phase so we beat the global / player-switch handler
    window.addEventListener('keydown', handleKeyPress, true);
    return () => window.removeEventListener('keydown', handleKeyPress, true);
  }, [hasStarted, compareMethod, showNarrowDownResults, labelA, labelB, selectedPlayer, selectPlayer, queue, eliminationPath]);

  const handleStartFree = () => {
    setHasStarted(true);
    const includedPlayers = getIncludedPlayers();
    if (includedPlayers.length === 0) return;

    const newPlayer = includedPlayers[Math.floor(Math.random() * includedPlayers.length)];
    selectPlayer(newPlayer);
    playAll();
  };

  const startNarrowDownWithPlayers = (players: number[]) => {
    if (players.length < 2) {
      toast({
        title: 'Need at least 2 tracks',
        description: 'Include at least two tracks to Narrow Down.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const shuffled = shuffleArray(players);
    const first = shuffled[0];
    const second = shuffled[1];
    const remaining = shuffled.slice(2);
    const labels = assignLabels(first, second);

    setQueue(remaining);
    setLabelA(labels.a);
    setLabelB(labels.b);
    setCurrentRound(1);
    setTotalRounds(players.length - 1);
    setEliminationPath([]);
    setNarrowDownWinner(null);
    setShowNarrowDownResults(false);
    setHasStarted(true);
    setCompareMethod('narrow-down');

    selectPlayer(Math.random() < 0.5 ? labels.a : labels.b);
    playAll();
  };

  const handleStartNarrowDown = () => {
    startNarrowDownWithPlayers(getIncludedPlayers());
  };

  const handleStart = () => {
    if (compareMethod === 'narrow-down') {
      handleStartNarrowDown();
    } else {
      handleStartFree();
    }
  };

  const handleStartComparing = () => {
    setIsComparing(true);
    const includedPlayers = getIncludedPlayers();
    if (includedPlayers.length === 0) return;

    const availablePlayers = allowRepeats 
      ? includedPlayers 
      : includedPlayers.filter(num => num !== selectedPlayer);

    const playersToChooseFrom = availablePlayers.length > 0 ? availablePlayers : includedPlayers;
    const newPlayer = playersToChooseFrom[Math.floor(Math.random() * playersToChooseFrom.length)];
    selectPlayer(newPlayer);
  };

  const handlePreference = (preference: 'better' | 'same' | 'worse') => {
    const currentTrack = audioElements.get(selectedPlayer)?.title || '';
    
    if (previousTrack) {
      setComparisons(prev => [...prev, {
        trackA: previousTrack,
        trackB: currentTrack,
        preference
      }]);
    }

    setPreviousTrack(currentTrack);
    
    const includedPlayers = getIncludedPlayers();
    if (includedPlayers.length === 0) return;

    const availablePlayers = allowRepeats 
      ? includedPlayers 
      : includedPlayers.filter(num => num !== selectedPlayer);

    const playersToChooseFrom = availablePlayers.length > 0 ? availablePlayers : includedPlayers;
    const newPlayer = playersToChooseFrom[Math.floor(Math.random() * playersToChooseFrom.length)];
    selectPlayer(newPlayer);
  };

  const handleSelectLabel = (label: 'A' | 'B') => {
    const player = label === 'A' ? labelA : labelB;
    if (player != null) {
      selectPlayer(player);
    }
  };

  const handlePrefer = (choice: 'A' | 'B', margin: 'clearly' | 'slightly') => {
    if (labelA == null || labelB == null) return;

    const winner = choice === 'A' ? labelA : labelB;
    const loser = choice === 'A' ? labelB : labelA;
    const winnerName = getTrackName(winner);
    const loserName = getTrackName(loser);
    const newPath: EliminationRound[] = [
      ...eliminationPath,
      {
        winner: winnerName,
        loser: loserName,
        margin,
        winnerPlayer: winner,
        loserPlayer: loser,
      },
    ];
    setEliminationPath(newPath);

    if (queue.length === 0) {
      setNarrowDownWinner(winnerName);
      setShowNarrowDownResults(true);
      pauseAll();
      return;
    }

    const nextChallenger = queue[0];
    const remaining = queue.slice(1);
    const labels = assignLabels(winner, nextChallenger);

    setQueue(remaining);
    setLabelA(labels.a);
    setLabelB(labels.b);
    setCurrentRound(r => r + 1);
    selectPlayer(Math.random() < 0.5 ? labels.a : labels.b);
  };

  const calculateStats = () => {
    const stats = new Map<string, TrackStats>();
    
    audioElements.forEach((audio, playerNumber) => {
      const isIncluded = blindTestIncludedPlayers.get(playerNumber) ?? true;
      if (isIncluded) {
        stats.set(audio.title, {
          better: 0,
          worse: 0,
          total: 0
        });
      }
    });

    comparisons.forEach(({ trackA, trackB, preference }) => {
      const statsA = stats.get(trackA);
      const statsB = stats.get(trackB);
      
      if (statsA && statsB) {
        if (preference === 'better') {
          statsB.better++;
          statsA.worse++;
        } else if (preference === 'worse') {
          statsA.better++;
          statsB.worse++;
        }
        statsA.total++;
        statsB.total++;
      }
    });

    return stats;
  };

  const copyTextToClipboard = async (text: string, description: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Results copied!',
        description,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy results to clipboard',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const copyResults = () => {
    const stats = calculateStats();
    let text = 'Pick Your Fave - Results\n\n';

    Array.from(stats)
      .sort(([_, statsA], [__, statsB]) => {
        const totalA = statsA.better + statsA.worse || 1;
        const totalB = statsB.better + statsB.worse || 1;
        const scoreA = (statsA.better / totalA) * 10;
        const scoreB = (statsB.better / totalB) * 10;
        return scoreB - scoreA;
      })
      .forEach(([track, stat]) => {
        const total = stat.better + stat.worse || 1;
        const score = ((stat.better / total) * 10).toFixed(1);
        text += `"${track}": ${score}\n`;
      });

    copyTextToClipboard(text, 'The comparison results have been copied to your clipboard');
  };

  const copyNarrowDownResults = () => {
    const runnerUps = Array.from(
      new Set(
        eliminationPath
          .filter(round => round.margin === 'slightly')
          .map(round => round.loser)
      )
    );

    let text = 'Pick Your Fave - Narrow Down\n\n';
    text += `Winner: ${narrowDownWinner}\n`;
    if (runnerUps.length > 0) {
      text += `Runner up: ${runnerUps.join(', ')}\n`;
    }
    text += '\n';

    if (eliminationPath.length > 0) {
      text += 'Breakdown:\n';
      eliminationPath.forEach((round, i) => {
        text += `  Round ${i + 1}: "${round.winner}" ${round.margin} beat "${round.loser}"\n`;
      });
    }

    copyTextToClipboard(text, 'The Narrow Down results have been copied to your clipboard');
  };

  const handleContinue = () => {
    setShowResults(false);
    setComparisons([]);
    setPreviousTrack(null);
    setHasStarted(false);
    setIsComparing(false);
  };

  const handlePlayAgainNarrowDown = () => {
    setHasStarted(false);
    resetNarrowDown();
  };

  const getFaceIconStyles = (color: string) => ({
    color: isComparing ? color : 'gray.500',
    cursor: isComparing ? 'pointer' : 'not-allowed',
    _hover: isComparing ? {
      color: color.replace('400', '300'),
      transform: 'scale(1.05)',
      borderColor: 'transparent',
      textShadow: `0 0 10px var(--chakra-colors-${color.replace('400', '400')})`
    } : {},
    _active: isComparing ? {
      transform: 'scale(0.95)',
      borderColor: 'transparent'
    } : {},
  });

  const labelButtonStyles = (isActive: boolean) => ({
    width: '80px',
    height: '80px',
    borderRadius: 'full',
    fontSize: '2xl',
    fontWeight: 'bold',
    bg: 'transparent',
    color: isActive ? 'accent.300' : 'gray.400',
    border: '2px solid',
    borderColor: isActive ? 'accent.400' : 'gray.600',
    textShadow: isActive ? '0 0 10px var(--chakra-colors-accent-400)' : 'none',
    _hover: {
      transform: 'scale(1.05)',
      bg: 'transparent',
      color: 'accent.300',
      borderColor: 'accent.400',
    },
    _active: {
      transform: 'scale(0.95)',
      bg: 'transparent',
    },
  });

  const methodButtonStyles = (isSelected: boolean) => ({
    size: 'sm' as const,
    colorScheme: 'accent' as const,
    variant: isSelected ? ('solid' as const) : ('outline' as const),
    bg: isSelected ? undefined : 'transparent',
    _hover: {
      transform: 'scale(1.02)',
    },
  });

  // Narrow Down results
  if (showNarrowDownResults && narrowDownWinner) {
    const trackPlayers = Array.from(
      new Set(eliminationPath.flatMap(round => [round.winnerPlayer, round.loserPlayer]))
    ).sort((a, b) => a - b);

    const runnerUps = Array.from(
      new Set(
        eliminationPath
          .filter(round => round.margin === 'slightly')
          .map(round => round.loser)
      )
    );

    const marginDot = (margin: 'clearly' | 'slightly') => (
      <Box
        w="10px"
        h="10px"
        borderRadius="full"
        bg={margin === 'clearly' ? 'green.400' : 'orange.400'}
        mx="auto"
        title={margin === 'clearly' ? 'Clearly better' : 'Slightly better'}
      />
    );

    return (
      <VStack spacing={6} align="stretch" w="full" p={2}>
        <Box overflowY="auto" px={4}>
          <VStack spacing={6} align="stretch">
            <VStack spacing={2} align="stretch">
              <Text color="gray.300" fontSize="sm">
                <Text as="span" color="accent.400" fontWeight="medium">Winner:</Text>{' '}
                {narrowDownWinner}
              </Text>
              {runnerUps.length > 0 && (
                <Text color="gray.300" fontSize="sm">
                  <Text as="span" color="accent.400" fontWeight="medium">Runner up:</Text>{' '}
                  {runnerUps.join(', ')}
                </Text>
              )}
            </VStack>

            {eliminationPath.length > 0 && (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th
                        position="sticky"
                        left={0}
                        zIndex={2}
                        bg="gray.800"
                        minW="60px"
                        boxShadow="inset -1px 0 0 var(--chakra-colors-gray-600)"
                      >
                        Track
                      </Th>
                      {eliminationPath.map((_, i) => (
                        <Th key={i} textAlign="center" whiteSpace="nowrap">
                          Round {i + 1}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {trackPlayers.map(playerNum => (
                      <Tr key={playerNum}>
                        <Td
                          position="sticky"
                          left={0}
                          zIndex={1}
                          bg="gray.800"
                          minW="60px"
                          boxShadow="inset -1px 0 0 var(--chakra-colors-gray-600)"
                        >
                          <Popover trigger="click" placement="right" isLazy>
                            <PopoverTrigger>
                              <Button
                                variant="link"
                                color="accent.400"
                                fontWeight="medium"
                                minW="auto"
                                height="auto"
                                p={0}
                                _hover={{ color: 'accent.300', textDecoration: 'underline' }}
                              >
                                {playerNum}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              bg="gray.700"
                              borderColor="gray.600"
                              maxW="280px"
                              _focus={{ outline: 'none', boxShadow: 'none' }}
                            >
                              <PopoverBody color="gray.100" fontSize="sm">
                                {getTrackName(playerNum)}
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        </Td>
                        {eliminationPath.map((round, i) => (
                          <Td key={i} textAlign="center">
                            {round.winnerPlayer === playerNum ? marginDot(round.margin) : null}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}

            <HStack spacing={4} justify="center" flexWrap="wrap" fontSize="xs" color="gray.500">
              <HStack spacing={2}>
                <Box w="8px" h="8px" borderRadius="full" bg="green.400" />
                <Text>Clearly better</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w="8px" h="8px" borderRadius="full" bg="orange.400" />
                <Text>Slightly better</Text>
              </HStack>
            </HStack>

            <VStack spacing={3} alignSelf="center">
              <Button
                onClick={handlePlayAgainNarrowDown}
                colorScheme="accent"
              >
                Play Again
              </Button>
              <Button
                leftIcon={<FaCopy />}
                onClick={copyNarrowDownResults}
                size="sm"
                colorScheme="accent"
                variant="outline"
              >
                Copy Results
              </Button>
            </VStack>
          </VStack>
        </Box>
      </VStack>
    );
  }

  // Free compare results
  if (showResults) {
    return (
      <VStack spacing={6} align="stretch" w="full" p={2}>
        <Box overflowY="auto" px={4}>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="xl" color="accent.400">
                Preference Results
              </Text>
              <Button
                leftIcon={<FaCopy />}
                onClick={copyResults}
                size="sm"
                colorScheme="accent"
                variant="outline"
              >
                Copy Results
              </Button>
            </HStack>

            <Text color="gray.300" fontSize="sm">
              Total Comparisons: {comparisons.length}
            </Text>

            <Table variant="simple" size="sm" maxW="container.md" mx="auto">
              <Thead>
                <Tr>
                  <Th width="80%">Track</Th>
                  <Th width="20%" isNumeric>Score</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Array.from(calculateStats())
                  .sort(([_, statsA], [__, statsB]) => {
                    const totalA = statsA.better + statsA.worse || 1;
                    const totalB = statsB.better + statsB.worse || 1;
                    const scoreA = (statsA.better / totalA) * 10;
                    const scoreB = (statsB.better / totalB) * 10;
                    return scoreB - scoreA;
                  })
                  .map(([track, stats]) => {
                    const total = stats.better + stats.worse || 1;
                    const score = ((stats.better / total) * 10).toFixed(1);
                    return (
                      <Tr key={track}>
                        <Td width="80%">{track}</Td>
                        <Td width="20%" isNumeric>{score}</Td>
                      </Tr>
                    );
                  })}
              </Tbody>
            </Table>

            <Button
              onClick={handleContinue}
              colorScheme="accent"
              alignSelf="center"
            >
              Continue Comparing
            </Button>
          </VStack>
        </Box>
      </VStack>
    );
  }

  // Narrow Down active session
  if (hasStarted && compareMethod === 'narrow-down') {
    const isListeningToA = selectedPlayer === labelA;
    const isListeningToB = selectedPlayer === labelB;

    const switchTooltip = (
      <HStack spacing={1} color="gray.800">
        <TooltipKeyBox>/</TooltipKeyBox>
        <Text as="span">or</Text>
        <TooltipKeyBox>1</TooltipKeyBox>
        <Text as="span">/</Text>
        <TooltipKeyBox>2</TooltipKeyBox>
        <Text as="span">to switch</Text>
      </HStack>
    );

    const clearlyTooltip = (
      <HStack spacing={1} color="gray.800">
        <TooltipKeyBox>↵</TooltipKeyBox>
      </HStack>
    );

    const slightlyTooltip = (
      <HStack spacing={1} color="gray.800">
        <TooltipKeyBox>⌥↵</TooltipKeyBox>
      </HStack>
    );

    const preferenceButtonStyles = (emphasis: 'clearly' | 'slightly') => ({
      colorScheme: 'accent' as const,
      variant: 'outline' as const,
      size: 'md' as const,
      w: '140px',
      h: '40px',
      fontSize: 'sm',
      color: emphasis === 'clearly' ? 'accent.300' : 'gray.400',
      borderColor: emphasis === 'clearly' ? 'accent.400' : 'gray.600',
      bg: 'transparent',
      _hover: {
        transform: 'scale(1.03)',
        borderColor: emphasis === 'clearly' ? 'accent.300' : 'gray.500',
        color: emphasis === 'clearly' ? 'accent.200' : 'gray.300',
        bg: 'transparent',
      },
      _active: {
        transform: 'scale(0.97)',
        bg: 'transparent',
      },
    });

    return (
      <VStack spacing={6} align="stretch" w="full" p={2}>
        <Center px={4}>
          <VStack spacing={1}>
            <Text color="accent.400" fontSize="sm" fontWeight="medium">
              Round {currentRound} of {totalRounds}
            </Text>
            <Text color="gray.300" fontSize="sm" textAlign="center">
              Switch between A and B, then pick the one you prefer
            </Text>
          </VStack>
        </Center>

        <HStack spacing={6} justify="center" align="flex-start" flex={1}>
          <VStack spacing={3} w="140px">
            <Tooltip label={switchTooltip} placement="top" hasArrow>
              <Button
                onClick={() => handleSelectLabel('A')}
                aria-label="Listen to A"
                {...labelButtonStyles(isListeningToA)}
              >
                A
              </Button>
            </Tooltip>
            <Text fontSize="sm" color={isListeningToA ? 'accent.400' : 'gray.500'}>
              {isListeningToA ? 'Playing' : 'Listen'}
            </Text>
            <Tooltip label={clearlyTooltip} placement="top" hasArrow>
              <Button
                onClick={() => handlePrefer('A', 'clearly')}
                aria-label="Clearly better A"
                {...preferenceButtonStyles('clearly')}
              >
                Clearly Better
              </Button>
            </Tooltip>
            <Tooltip label={slightlyTooltip} placement="top" hasArrow>
              <Button
                onClick={() => handlePrefer('A', 'slightly')}
                aria-label="Slightly better A"
                {...preferenceButtonStyles('slightly')}
              >
                Slightly Better
              </Button>
            </Tooltip>
          </VStack>

          <VStack spacing={3} w="140px">
            <Tooltip label={switchTooltip} placement="top" hasArrow>
              <Button
                onClick={() => handleSelectLabel('B')}
                aria-label="Listen to B"
                {...labelButtonStyles(isListeningToB)}
              >
                B
              </Button>
            </Tooltip>
            <Text fontSize="sm" color={isListeningToB ? 'accent.400' : 'gray.500'}>
              {isListeningToB ? 'Playing' : 'Listen'}
            </Text>
            <Tooltip label={clearlyTooltip} placement="top" hasArrow>
              <Button
                onClick={() => handlePrefer('B', 'clearly')}
                aria-label="Clearly better B"
                {...preferenceButtonStyles('clearly')}
              >
                Clearly Better
              </Button>
            </Tooltip>
            <Tooltip label={slightlyTooltip} placement="top" hasArrow>
              <Button
                onClick={() => handlePrefer('B', 'slightly')}
                aria-label="Slightly better B"
                {...preferenceButtonStyles('slightly')}
              >
                Slightly Better
              </Button>
            </Tooltip>
          </VStack>
        </HStack>
      </VStack>
    );
  }

  // Start screen + Free compare session
  return (
    <VStack spacing={6} align="stretch" w="full" p={2}>
      {hasStarted && (
        <Center px={4}>
          <Text color="gray.300" fontSize="sm" textAlign="center">
            {!isComparing ? (
              <>
                Listen to the audio now playing<br />
                When you're ready, click <Text as="span" color="accent.400" display="inline">Start Comparing</Text> to switch tracks
              </>
            ) : (
              "How do you feel about this track compared to the one before?"
            )}
          </Text>
        </Center>
      )}

      <VStack spacing={4} flex={1} justify="center">
        {!hasStarted && (
          <HStack spacing={2} justify="center">
            <Tooltip
              label="Open-ended comparisons with scores"
              placement="top"
              hasArrow
            >
              <Button
                onClick={() => setCompareMethod('free')}
                {...methodButtonStyles(compareMethod === 'free')}
              >
                Compare
              </Button>
            </Tooltip>
            <Tooltip
              label="Knockout rounds until one favorite remains"
              placement="top"
              hasArrow
            >
              <Button
                onClick={() => setCompareMethod('narrow-down')}
                {...methodButtonStyles(compareMethod === 'narrow-down')}
              >
                Narrow Down
              </Button>
            </Tooltip>
          </HStack>
        )}

        {compareMethod === 'free' && hasStarted && (
          <HStack spacing={4} justify="center" visibility={isComparing ? "visible" : "hidden"}>
            <VStack spacing={2}>
              <Button
                onClick={() => isComparing && handlePreference('worse')}
                size="lg"
                variant="unstyled"
                aria-label="Worse"
                height="auto"
                {...getFaceIconStyles('red.400')}
              >
                <Icon 
                  as={FaFrown} 
                  boxSize={12}
                  sx={{
                    strokeWidth: '24px',
                    fill: 'none',
                    stroke: 'currentColor'
                  }}
                />
              </Button>
              <Text fontSize="sm" color="red.400">Worse</Text>
            </VStack>
            <VStack spacing={2}>
              <Button
                onClick={() => isComparing && handlePreference('same')}
                size="lg"
                variant="unstyled"
                aria-label="Same"
                height="auto"
                {...getFaceIconStyles('yellow.400')}
              >
                <Icon 
                  as={FaMeh} 
                  boxSize={12}
                  sx={{
                    strokeWidth: '24px',
                    fill: 'none',
                    stroke: 'currentColor'
                  }}
                />
              </Button>
              <Text fontSize="sm" color="yellow.400">Same</Text>
            </VStack>
            <VStack spacing={2}>
              <Button
                onClick={() => isComparing && handlePreference('better')}
                size="lg"
                variant="unstyled"
                aria-label="Better"
                height="auto"
                {...getFaceIconStyles('green.400')}
              >
                <Icon 
                  as={FaSmile} 
                  boxSize={12}
                  sx={{
                    strokeWidth: '24px',
                    fill: 'none',
                    stroke: 'currentColor'
                  }}
                />
              </Button>
              <Text fontSize="sm" color="green.400">Better</Text>
            </VStack>
          </HStack>
        )}

        <Button
          onClick={
            hasStarted
              ? (isComparing ? () => setShowResults(true) : handleStartComparing)
              : handleStart
          }
          colorScheme="accent"
          variant="outline"
          size="lg"
          width="200px"
          mx="auto"
          isDisabled={hasStarted && isComparing && comparisons.length === 0}
          mt={hasStarted ? 6 : 2}
          _hover={{
            transform: 'scale(1.05)',
            borderColor: 'accent.400',
            bg: 'transparent'
          }}
          _active={{
            transform: 'scale(0.95)',
            borderColor: 'accent.500',
            bg: 'transparent'
          }}
        >
          {hasStarted ? (isComparing ? "Show Results" : "Start Comparing") : "Start"}
        </Button>

        {!hasStarted && (
          <Tooltip 
            label={allowRepeats ? "Random switches may select the same track again" : "Random switches will always select a different track"} 
            placement="top"
            hasArrow
            isDisabled={compareMethod !== 'free'}
          >
            <FormControl
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={2}
              visibility={compareMethod === 'free' ? 'visible' : 'hidden'}
              pointerEvents={compareMethod === 'free' ? 'auto' : 'none'}
            >
              <FormLabel htmlFor="allow-repeats" mb={0} color="gray.300" fontSize="sm" cursor="help">
                Allow repeats
              </FormLabel>
              <Switch
                id="allow-repeats"
                isChecked={allowRepeats}
                onChange={(e) => setAllowRepeats(e.target.checked)}
                colorScheme="accent"
              />
            </FormControl>
          </Tooltip>
        )}

        {hasStarted && compareMethod === 'free' && (
          <Tooltip 
            label={allowRepeats ? "Random switches may select the same track again" : "Random switches will always select a different track"} 
            placement="top"
            hasArrow
          >
            <FormControl display="flex" alignItems="center" justifyContent="center" mt={4}>
              <FormLabel htmlFor="allow-repeats-active" mb={0} color="gray.300" fontSize="sm" cursor="help">
                Allow repeats
              </FormLabel>
              <Switch
                id="allow-repeats-active"
                isChecked={allowRepeats}
                onChange={(e) => setAllowRepeats(e.target.checked)}
                colorScheme="accent"
              />
            </FormControl>
          </Tooltip>
        )}
      </VStack>
    </VStack>
  );
};

export default PickYourFaveView;
